var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { XbpmContext, formatNavParams, FormUtils } from '@unitarius/xbpm-runtime';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { nanoid } from 'nanoid';
import { equals } from 'ramda';
const MIN_HEIGHT = 300;
const { PUBLIC_URL } = process.env;
console.log('PUBLIC_URL is ', PUBLIC_URL);
export class XbpmBridgeClientService {
    constructor(config, pubSub, root, params = {}, notResizeModal = false) {
        this.config = config;
        this.pubSub = pubSub;
        this.root = root;
        this.notResizeModal = notResizeModal;
        this.observeHeight = () => {
            const heightObserver$ = new BehaviorSubject(MIN_HEIGHT);
            const updateHeight = () => {
                const height = document.body.scrollHeight < MIN_HEIGHT ? MIN_HEIGHT : document.body.scrollHeight;
                heightObserver$.next(height);
            };
            setInterval(updateHeight, 100);
            // @TODO remove hardcoded instanceId
            if (!this.notResizeModal || this.instanceId === 'start') {
                heightObserver$
                    .pipe(distinctUntilChanged(equals))
                    .subscribe(height => this.publish('resize', { targetId: 'root', height }));
            }
        };
        this.toSourceEvent = (event) => (Object.assign(Object.assign({}, event), { sourceId: this.instanceId }));
        this.publish = (type, event) => this.pubSub.publish(type, this.toSourceEvent(event), this.root);
        this.subscribe = (type, listener) => this.pubSub.subscribe(type, event => {
            if (event.payload.targetId === this.instanceId) {
                listener(event);
            }
        });
        this.getInstanceUrl = (instanceId = nanoid(), params) => {
            const _a = params || {}, { baseUrl } = _a, urlParams = __rest(_a, ["baseUrl"]);
            const base = `${window.location.origin}${PUBLIC_URL}/`;
            if (urlParams && urlParams.action) {
                const { action, actionParams, applicationCode, applicationGroup, applicationVersion } = urlParams;
                return `${base}?applicationGroup=${applicationGroup}&applicationCode=${applicationCode}&applicationVersion=${applicationVersion}&instanceId=${instanceId}&action=${action}&actionParams=${JSON.stringify(actionParams)}`;
            }
            const paramEntries = Object.entries(Object.assign(Object.assign({}, (urlParams || {})), { instanceId }));
            return encodeURI(`${base}?${paramEntries.map(([k, v]) => `${k}=${v}`).join('&')}`);
        };
        // modal/popup
        this.open = (event) => {
            const instanceId = event.targetId || nanoid();
            const instanceUrl = event.url || this.getInstanceUrl(instanceId, event.params);
            this.publish('open', Object.assign(Object.assign({}, event), { params: Object.assign(Object.assign({}, event.params), { url: instanceUrl }), targetId: 'root', instanceId }));
            return instanceId;
        };
        this.close = (event) => setImmediate(() => {
            const instanceId = (event && event.targetId) || this.instanceId;
            this.publish('close', Object.assign(Object.assign({}, (event || {})), { targetId: 'root', instanceId }));
        });
        // navigation
        this.navigate = (event) => this.publish(event['url'] ? 'location' : 'navigate', event);
        this.home = (event) => this.publish('home', event);
        this.reload = (event) => this.publish('reload', event);
        // form changes
        this.changes = (event) => this.publish('changes', event);
        // send data
        this.send = (event) => this.publish('data', event);
        // wait for target to send data
        this.receiving = (event) => new Promise((resolve, reject) => {
            const subcriptions = [
                // cancel on target unloaded
                this.subscribe('unloaded', ({ payload }) => {
                    if (payload.sourceId === event.targetId) {
                        unsubscribe();
                        reject(FormUtils.result.cancelation());
                    }
                }),
                // receiving data
                this.subscribe('data', ({ payload }) => {
                    if (payload.sourceId === event.targetId) {
                        unsubscribe();
                        resolve(payload.data);
                    }
                }),
            ];
            const unsubscribe = () => subcriptions.forEach(sub => sub.unsubscribe());
        });
        // wait for target to close
        this.closing = (event) => new Promise((resolve, reject) => {
            const subcriptions = [
                // cancel on target unloaded
                this.subscribe('unloaded', ({ payload }) => {
                    if (payload.sourceId === event.targetId) {
                        unsubscribe();
                        reject(FormUtils.result.cancelation());
                    }
                }),
                // target closing
                this.subscribe('closed', ({ payload }) => {
                    if (payload.sourceId === event.targetId) {
                        unsubscribe();
                        resolve(true);
                    }
                }),
            ];
            const unsubscribe = () => subcriptions.forEach(sub => sub.unsubscribe());
        });
        // wait for target to unload
        this.unloading = (event) => new Promise(resolve => {
            const { unsubscribe } = this.subscribe('unloaded', ({ payload }) => {
                if (payload.sourceId === event.targetId) {
                    unsubscribe();
                    resolve(true);
                }
            });
        });
        // wait for any frame to unload
        this.anyUnload = (event) => new Promise(resolve => {
            const { unsubscribe } = this.pubSub.subscribe('*', e => {
                if (['closed', 'unloaded'].includes(e.type)) {
                    console.info('[BRIDGE] - Some iframe unloaded');
                    unsubscribe();
                    resolve(true);
                }
            });
        });
        this.initialize(params);
        // subscriptions
        // navigation
        this.subscribe('navigate', event => XbpmContext.getContext().navService.navigate(formatNavParams(event.payload)));
        this.subscribe('location', event => (location.href = event.payload.url));
        this.subscribe('home', () => XbpmContext.getContext().navService.home());
        this.subscribe('reload', () => location.reload());
        // changes
        this.subscribe('changes', event => XbpmContext.getContext().formContext.handleChanges(event.payload));
        // restore context
        this.subscribe('restore', event => this.initialize(event.payload));
        // bridge ready event
        this.publish('ready', { targetId: 'root' });
        // iframe height observer
        if (!window.opener && root !== window) {
            this.observeHeight();
        }
        // emit window unload
        window.addEventListener('unload', () => this.publish('unload', { targetId: 'root' }));
    }
    initialize(params) {
        this._instanceId = params.instanceId || this._instanceId || nanoid();
        this._params = Object.freeze(params || {});
        const navParams = formatNavParams(this.params);
        if (navParams) {
            XbpmContext.getContext().storeNavigationParams(navParams);
        }
    }
    get instanceId() {
        return this._instanceId;
    }
    get params() {
        return this._params;
    }
}
//# sourceMappingURL=xbpm-bridge-client.service.js.map